/** Viewport content that blocks iOS auto-zoom on focused inputs. Pinch-zoom still works on iOS 10+. */
export const IOS_NO_INPUT_ZOOM_VIEWPORT =
  'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';

const IOS_SCALE_NUDGE_VIEWPORT = 'width=device-width, initial-scale=1, maximum-scale=1.01, viewport-fit=cover';

let recoveringScale = false;

export function isIosTouchDevice() {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isCapacitorIos() {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  return (
    cap != null &&
    typeof cap.isNativePlatform === 'function' &&
    cap.isNativePlatform() &&
    typeof cap.getPlatform === 'function' &&
    cap.getPlatform() === 'ios'
  );
}

export function isCapacitorAndroid() {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  return (
    cap != null &&
    typeof cap.isNativePlatform === 'function' &&
    cap.isNativePlatform() &&
    typeof cap.getPlatform === 'function' &&
    cap.getPlatform() === 'android'
  );
}

function isTextInput(el) {
  if (!el || el === document.body || el === document.documentElement) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true;
}

/**
 * Apply no-auto-zoom viewport.
 * @param {{ permanent?: boolean }} [options] permanent: do not restore on cleanup (native iOS app).
 */
export function lockIosInputAutoZoom(options = {}) {
  if (typeof document === 'undefined') return () => {};
  const viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) return () => {};
  const previous = viewport.getAttribute('content');
  viewport.setAttribute('content', IOS_NO_INPUT_ZOOM_VIEWPORT);
  return () => {
    if (options.permanent) return;
    if (previous != null) {
      viewport.setAttribute('content', previous);
    }
  };
}

/** Undo WKWebView / iOS scroll-on-focus that slides fixed chrome down. */
export function resetIosFocusScroll() {
  if (typeof window === 'undefined') return;
  window.scrollTo(0, 0);
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
  const main = document.getElementById('layoutMainScroll');
  if (main) main.scrollTop = 0;
  const list = document.querySelector('.listPageRoot');
  if (list) list.scrollTop = 0;
}

/**
 * If WKWebView locked a zoomed scale (re-setting viewport during focus), nudge it back to 1.
 * Safe to call on blur / after keyboard hide — do not call while the input is still focusing.
 */
export function recoverIosViewportScale() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (recoveringScale) return;
  const vv = window.visualViewport;
  const scale = vv?.scale ?? 1;
  const offset = vv?.offsetTop || 0;
  if (Math.abs(scale - 1) < 0.02) {
    if (offset < 1) {
      document.documentElement.style.setProperty('--ios-vv-offset', '0px');
    }
    resetIosFocusScroll();
    return;
  }

  const viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    resetIosFocusScroll();
    return;
  }

  recoveringScale = true;
  viewport.setAttribute('content', IOS_SCALE_NUDGE_VIEWPORT);
  requestAnimationFrame(() => {
    viewport.setAttribute('content', IOS_NO_INPUT_ZOOM_VIEWPORT);
    resetIosFocusScroll();
    document.documentElement.style.setProperty('--ios-vv-offset', '0px');
    recoveringScale = false;
  });
}

/** Keep --ios-vv-offset in sync so fixed header/search can cancel visualViewport pan. */
export function bindIosVisualViewportLock() {
  if (typeof window === 'undefined') return () => {};
  const vv = window.visualViewport;
  if (!vv) return () => {};

  const sync = () => {
    if (recoveringScale) return;
    const offset = vv.offsetTop || 0;
    document.documentElement.style.setProperty('--ios-vv-offset', `${offset}px`);
    if (offset) {
      resetIosFocusScroll();
    }
    const inputFocused = isTextInput(document.activeElement);
    if (!inputFocused && vv.scale && Math.abs(vv.scale - 1) > 0.02) {
      recoverIosViewportScale();
    }
  };

  vv.addEventListener('scroll', sync);
  vv.addEventListener('resize', sync);
  window.addEventListener('scroll', sync, { passive: true });
  sync();
  return () => {
    vv.removeEventListener('scroll', sync);
    vv.removeEventListener('resize', sync);
    window.removeEventListener('scroll', sync);
    document.documentElement.style.removeProperty('--ios-vv-offset');
  };
}
